/**
 * Client-side search relevance helpers: multi-token match scoring without changing URLs or filters.
 * Used by header suggest, predictive search modal, search results page, and optional legacy header search.
 */
(function (global) {
  const ACCENT = /\p{M}/gu;

  function normalize(str) {
    return String(str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(ACCENT, '');
  }

  /**
   * Meaningful tokens (min length 2) for multi-word ranking.
   * @param {string} q
   * @returns {string[]}
   */
  function tokenizeQuery(q) {
    return normalize(q)
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.length >= 2);
  }

  function countTokenMatches(haystackNorm, tokens) {
    let n = 0;
    for (let i = 0; i < tokens.length; i++) {
      if (haystackNorm.includes(tokens[i])) n++;
    }
    return n;
  }

  function haystackFromSuggestProduct(product) {
    const type = product.product_type || product.type;
    const tagStr = Array.isArray(product.tags) ? product.tags.join(' ') : '';
    const parts = [
      product.title,
      type,
      product.vendor,
      product.body,
      tagStr,
      ...(product.variants || []).map((v) => [v.title, v.sku].filter(Boolean).join(' ')),
    ];
    return normalize(parts.join(' '));
  }

  /**
   * Stable sort: more tokens matched → all tokens matched → more title matches → original index.
   * @param {object[]} products
   * @param {string} query
   * @returns {object[]}
   */
  function sortProductsByQuery(products, query) {
    const tokens = tokenizeQuery(query);
    if (tokens.length <= 1) return products;

    return products
      .map((p, index) => {
        const hay = haystackFromSuggestProduct(p);
        const matches = countTokenMatches(hay, tokens);
        const titleNorm = normalize(p.title);
        const titleMatches = countTokenMatches(titleNorm, tokens);
        const all = matches === tokens.length ? 1 : 0;
        const score = all * 1e9 + matches * 1e6 + titleMatches * 1e3 - index * 0.001;
        return { p, score };
      })
      .sort((a, b) => b.score - a.score)
      .map((x) => x.p);
  }

  function cardHaystackMatchCount(card, tokens) {
    const blob = card.getAttribute('data-madesa-search-blob') || '';
    const titleEl = card.querySelector('.madesa-product-title');
    const titleText = titleEl ? titleEl.textContent : '';
    const hay = normalize(blob ? `${blob} ${titleText}` : titleText);
    return countTokenMatches(hay, tokens);
  }

  function tokenizeFetchPhrases(originalQ) {
    return String(originalQ || '')
      .trim()
      .split(/\s+/)
      .map((s) => s.trim())
      .filter((s) => normalize(s).length >= 2);
  }

  function handleize(str) {
    return normalize(str)
      .replace(/['']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /** @param {string} q */
  function normalizeSearchQuery(q) {
    return normalize(String(q || '').trim());
  }

  function getSearchBaseUrl() {
    const routes = global.Theme && global.Theme.routes;
    return routes && routes.search_url ? routes.search_url : '/search';
  }

  /** @returns {string[]} Normalized handle/title prefixes for line collections (locale via Theme.madesaSearch). */
  function getCollectionLinePrefixes() {
    const fromTheme = global.Theme && global.Theme.madesaSearch && global.Theme.madesaSearch.collectionLinePrefixes;
    if (Array.isArray(fromTheme) && fromTheme.length) {
      const out = [];
      for (let i = 0; i < fromTheme.length; i++) {
        const p = normalize(String(fromTheme[i] || '').trim());
        if (p) out.push(p);
      }
      if (out.length) return out;
    }
    return [];
  }

  /**
   * @param {string} prefixNorm normalized prefix (e.g. linha, linea)
   * @param {string} tokenNorm single-word query token
   */
  function prefixedLineTitleNorm(prefixNorm, tokenNorm) {
    return `${prefixNorm} ${tokenNorm}`;
  }

  /**
   * @param {string} prefixNorm
   * @param {string} tokenHandle handleized single-word query
   */
  function prefixedLineHandle(prefixNorm, tokenHandle) {
    const ph = handleize(prefixNorm);
    if (!ph || !tokenHandle) return '';
    return `${ph}-${tokenHandle}`;
  }

  /**
   * prefix=last only for multi-word queries (AND + prefix on last token).
   * Single-word searches use exact token matching and stay case-stable via normalizeSearchQuery.
   * @param {URLSearchParams} params
   * @param {string} rawQuery
   */
  function applyProductSearchParams(params, rawQuery) {
    const trimmed = String(rawQuery || '').trim();
    const qNorm = normalizeSearchQuery(trimmed) || trimmed.toLowerCase();
    params.set('q', qNorm);
    params.set('type', 'product');
    params.delete('options[prefix]');
    if (tokenizeQuery(trimmed).length >= 2) {
      params.set('options[prefix]', 'last');
    }
  }

  /**
   * @param {string} rawQuery
   * @param {string} [collectionHandle]
   * @returns {string}
   */
  function buildProductSearchUrl(rawQuery, collectionHandle) {
    const params = new URLSearchParams();
    applyProductSearchParams(params, rawQuery);
    if (collectionHandle) {
      params.set('collection_handle', String(collectionHandle));
    }
    const qs = params.toString();
    return qs ? `${getSearchBaseUrl()}?${qs}` : getSearchBaseUrl();
  }

  /**
   * @param {string} title
   * @param {string} queryNorm
   */
  function collectionTitleMatchesQuery(title, queryNorm) {
    const titleNorm = normalize(title);
    if (titleNorm === queryNorm) return true;
    const tokens = queryNorm.split(/\s+/).filter(Boolean);
    if (tokens.length === 1) {
      const prefixes = getCollectionLinePrefixes();
      for (let i = 0; i < prefixes.length; i++) {
        if (titleNorm === prefixedLineTitleNorm(prefixes[i], tokens[0])) return true;
      }
    }
    return false;
  }

  /**
   * @param {string} handle
   * @param {string} rawQuery
   */
  function collectionHandleMatchesQuery(handle, rawQuery) {
    const h = String(handle || '').toLowerCase();
    const qh = handleize(rawQuery);
    if (!qh) return false;
    if (h === qh) return true;
    const prefixes = getCollectionLinePrefixes();
    for (let i = 0; i < prefixes.length; i++) {
      const candidate = prefixedLineHandle(prefixes[i], qh);
      if (candidate && h === candidate) return true;
    }
    return false;
  }

  /**
   * @param {string} handle
   * @returns {Promise<string|null>} collection handle if it exists
   */
  async function probeCollectionHandle(handle) {
    if (!handle) return null;
    try {
      const res = await fetch(`/collections/${encodeURIComponent(handle)}.json`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const col = data && data.collection;
      if (!col || !col.handle) return null;
      return col.handle;
    } catch {
      return null;
    }
  }

  /**
   * Match a collection handle for the query (title/handle/prefixed line names).
   * @param {string} rawQuery
   * @returns {Promise<string|null>}
   */
  async function getCollectionHandleForQuery(rawQuery) {
    const q = String(rawQuery || '').trim();
    if (!q) return null;
    const queryNorm = normalizeSearchQuery(q);
    if (!queryNorm) return null;

    const handlesToTry = new Set();
    const qh = handleize(q);
    if (qh) {
      handlesToTry.add(qh);
      const prefixes = getCollectionLinePrefixes();
      for (let i = 0; i < prefixes.length; i++) {
        const candidate = prefixedLineHandle(prefixes[i], qh);
        if (candidate) handlesToTry.add(candidate);
      }
    }

    try {
      const suggestQ = queryNorm || q.toLowerCase();
      const suggestUrl = `/search/suggest.json?q=${encodeURIComponent(suggestQ)}&resources[type]=collection&resources[limit]=10`;
      const res = await fetch(suggestUrl, { credentials: 'same-origin' });
      if (res.ok) {
        const data = await res.json();
        const collections = data.resources?.results?.collections || [];
        for (let i = 0; i < collections.length; i++) {
          const c = collections[i];
          if (collectionTitleMatchesQuery(c.title, queryNorm)) {
            return c.handle || null;
          }
          if (collectionHandleMatchesQuery(c.handle, q)) {
            return c.handle || null;
          }
          handlesToTry.delete(String(c.handle || '').toLowerCase());
        }
      }
    } catch (e) {
      console.warn('[MadesaSearch] getCollectionHandleForQuery suggest', e);
    }

    for (const handle of handlesToTry) {
      const found = await probeCollectionHandle(handle);
      if (found) return found;
    }
    return null;
  }

  /** @param {URLSearchParams} params */
  function searchUrlHasActiveFilters(params) {
    for (const key of params.keys()) {
      if (key.startsWith('filter.')) return true;
    }
    return false;
  }

  /**
   * @param {string} rawQuery
   * @returns {Promise<void>}
   */
  async function navigateForSearchQuery(rawQuery) {
    const q = String(rawQuery || '').trim();
    if (!q) return;
    const collectionHandle = await getCollectionHandleForQuery(q);
    global.location.href = buildProductSearchUrl(q, collectionHandle || undefined);
  }

  /**
   * Normalize q in the URL and attach collection_handle when the term matches a collection.
   * Stays on /search (does not redirect to /collections/...).
   */
  async function initSearchPageUrlSync() {
    try {
      const path = global.location.pathname || '';
      const pathParts = path.split('/').filter(Boolean);
      if (pathParts[pathParts.length - 1] !== 'search') return;

      const params = new URLSearchParams(global.location.search);
      if (params.get('type') === 'article') return;

      const q = params.get('q');
      if (!q || !q.trim()) return;

      if (params.get('page') && Number(params.get('page')) > 1) return;
      if (searchUrlHasActiveFilters(params)) return;

      const before = params.toString();
      applyProductSearchParams(params, q);

      if (!params.get('collection_handle')) {
        const handle = await getCollectionHandleForQuery(params.get('q') || q);
        if (handle) {
          params.set('collection_handle', handle);
        }
      }

      if (params.toString() !== before) {
        global.location.replace(`${path}?${params.toString()}`);
      }
    } catch (e) {
      console.warn('[MadesaSearch] initSearchPageUrlSync', e);
    }
  }

  /**
   * @param {string} newQ
   * @param {string} sectionId
   */
  function buildSectionSearchFetchUrl(newQ, sectionId) {
    const u = new URL(window.location.href);
    const sid = sectionId.replace(/^shopify-section-/, '');
    for (const [k] of [...u.searchParams.entries()]) {
      if (
        k === 'section_id' ||
        k === 'q' ||
        k === 'page' ||
        k === 'type' ||
        k === 'options[prefix]' ||
        k === 'collection_handle'
      ) {
        u.searchParams.delete(k);
      }
    }
    applyProductSearchParams(u.searchParams, newQ);
    u.searchParams.set('section_id', sid);
    return u.toString();
  }

  /**
   * @param {string} html
   * @param {string} sectionId
   * @returns {HTMLElement[]}
   */
  function parseSectionSearchProductCards(html, sectionId) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sid = sectionId.replace(/^shopify-section-/, '');
    const sec =
      doc.getElementById(`shopify-section-${sid}`) || doc.querySelector(`#shopify-section-${sid}`);
    const innerList =
      sec?.querySelector('#madesa-product-list') || sec?.querySelector('.madesa-product-list');
    if (!innerList) return [];
    return Array.from(innerList.querySelectorAll(':scope > .madesa-product-card'));
  }

  function removeSupplementCards(list) {
    list.querySelectorAll(':scope > .madesa-product-card[data-madesa-search-supplement="1"]').forEach((n) => {
      n.remove();
    });
  }

  /**
   * Reorders cards already in the DOM (includes supplementary items by term).
   * @param {HTMLElement} list
   */
  function sortMadesaSearchProductCards(list) {
    const params = new URLSearchParams(global.location.search);
    const q = params.get('q');
    if (!list || !q || !q.trim()) return;

    const tokens = tokenizeQuery(q);
    if (tokens.length <= 1) return;

    const cards = Array.from(list.querySelectorAll(':scope > .madesa-product-card'));
    if (cards.length <= 1) return;

    const scored = cards.map((card, index) => {
      const blob = card.getAttribute('data-madesa-search-blob') || '';
      const titleEl = card.querySelector('.madesa-product-title');
      const titleText = titleEl ? titleEl.textContent : '';
      const hay = normalize(blob ? `${blob} ${titleText}` : titleText);
      const titleNorm = normalize(titleText);
      const matches = countTokenMatches(hay, tokens);
      const titleMatches = countTokenMatches(titleNorm, tokens);
      const all = matches === tokens.length ? 1 : 0;
      const score = all * 1e9 + matches * 1e6 + titleMatches * 1e3 - index * 0.001;
      return { card, score };
    });

    scored.sort((a, b) => b.score - a.score);

    const newOrderKey = scored
      .map(({ card }) => card.dataset.productId || card.id || '')
      .join('\u001d');
    const currentOrderKey = cards
      .map((card) => card.dataset.productId || card.id || '')
      .join('\u001d');
    if (newOrderKey === currentOrderKey) return;

    list.querySelectorAll(':scope > .madesa-search-results-section-heading').forEach((h) => h.remove());

    const frag = document.createDocumentFragment();
    for (let i = 0; i < scored.length; i++) frag.appendChild(scored[i].card);
    list.appendChild(frag);
  }

  /** @type {number | undefined} */
  let supplementTimer;
  /** @type {number} */
  const SUPPLEMENT_MAX = 36;

  /**
   * Additional search for each word (Shopify tends to use AND in full searches).
   * Includes products that only partially match the terms, without changing the URL.
   * @param {HTMLElement} list
   */
  async function supplementPartialSearchResults(list) {
    if (!(list instanceof HTMLElement)) return;
    if (list.dataset.madesaSupplementBusy === '1') return;
    if (global.Shopify?.designMode) return;

    const params = new URLSearchParams(global.location.search);
    const q = params.get('q') || '';
    const scoringTokens = tokenizeQuery(q);
    if (scoringTokens.length < 2) return;

    const page = params.get('page');
    if (page && Number(page) > 1) return;

    const root = document.querySelector('results-list.madesa-search-results');
    const sectionId = root?.getAttribute('section-id');
    if (!sectionId) return;

    list.dataset.madesaSupplementBusy = '1';
    try {
      removeSupplementCards(list);

      const baseIds = new Set(
        Array.from(list.querySelectorAll(':scope > .madesa-product-card')).map(
          (c) => c.dataset.productId || ''
        )
      );
      baseIds.delete('');

      const phrases = tokenizeFetchPhrases(q);
      if (phrases.length < 2) return;

      const seen = new Set(baseIds);
      let added = 0;
      const batch = document.createDocumentFragment();

      for (let p = 0; p < phrases.length && added < SUPPLEMENT_MAX; p++) {
        const phrase = phrases[p];
        const url = buildSectionSearchFetchUrl(phrase, sectionId);
        const res = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!res.ok) continue;
        const html = await res.text();
        const remoteCards = parseSectionSearchProductCards(html, sectionId);

        for (let i = 0; i < remoteCards.length && added < SUPPLEMENT_MAX; i++) {
          const src = remoteCards[i];
          const id = src.dataset?.productId || '';
          if (!id || seen.has(id)) continue;

          const m = cardHaystackMatchCount(src, scoringTokens);
          if (m <= 0 || m >= scoringTokens.length) continue;

          const clone = /** @type {HTMLElement} */ (src.cloneNode(true));
          clone.setAttribute('data-madesa-search-supplement', '1');
          batch.appendChild(clone);
          seen.add(id);
          added++;
        }
      }

      if (added > 0) {
        list.appendChild(batch);
        sortMadesaSearchProductCards(list);
        syncSearchResultSectionHeadings(list);
      }
    } catch (e) {
      console.warn('[MadesaSearch] supplementPartialSearchResults', e);
    } finally {
      list.dataset.madesaSupplementBusy = '0';
    }
  }

  function scheduleSupplement(list) {
    if (!(list instanceof HTMLElement)) return;
    clearTimeout(supplementTimer);
    supplementTimer = global.setTimeout(() => {
      supplementPartialSearchResults(list);
    }, 450);
  }

  /**
   * Visual-only: headings for "specific" (all tokens in the haystack) vs "general" (all others).
   * Does not alter order; assumes the current order of the list.
   * @param {HTMLElement} list
   */
  function tSearchHeading(key) {
    const tr = global.Theme?.translations;
    const v = tr && typeof tr[key] === 'string' ? tr[key].trim() : '';
    if (v) return v;
    if (key === 'search_results_specific_title') return 'Resultados específicos';
    if (key === 'search_results_general_title') return 'Resultados gerais';
    return key;
  }

  function syncSearchResultSectionHeadings(list) {
    if (!(list instanceof HTMLElement)) return;

    const params = new URLSearchParams(global.location.search);
    const q = params.get('q') || '';
    const tokens = tokenizeQuery(q);
    const cards = Array.from(list.querySelectorAll(':scope > .madesa-product-card'));

    if (tokens.length <= 1 || cards.length === 0) {
      delete list.dataset.madesaSearchHeadingSig;
      list.querySelectorAll(':scope > .madesa-search-results-section-heading').forEach((el) => el.remove());
      return;
    }

    const full = tokens.length;
    let splitAt = cards.length;
    for (let i = 0; i < cards.length; i++) {
      if (cardHaystackMatchCount(cards[i], tokens) < full) {
        splitAt = i;
        break;
      }
    }

    const cardIds = cards.map((c) => c.dataset.productId || '').join('\u001d');
    const nextSig = `${splitAt}|${full}|${cardIds}`;
    if (list.dataset.madesaSearchHeadingSig === nextSig) return;
    list.dataset.madesaSearchHeadingSig = nextSig;

    list.querySelectorAll(':scope > .madesa-search-results-section-heading').forEach((el) => el.remove());

    const mk = (text) => {
      const h = document.createElement('h2');
      h.className = 'madesa-search-results-section-heading';
      h.textContent = text;
      return h;
    };

    if (splitAt === 0) {
      list.insertBefore(mk(tSearchHeading('search_results_general_title')), cards[0]);
      return;
    }
    if (splitAt === cards.length) {
      list.insertBefore(mk(tSearchHeading('search_results_specific_title')), cards[0]);
      return;
    }
    list.insertBefore(mk(tSearchHeading('search_results_specific_title')), cards[0]);
    list.insertBefore(mk(tSearchHeading('search_results_general_title')), cards[splitAt]);
  }

  /**
    * Reorders search cards + section titles (specific/general).
    * The term complement runs on a different timer (scheduleSupplement).
  */
  function reorderMadesaSearchCards() {
    const root = document.querySelector('results-list.madesa-search-results');
    const list = root?.querySelector('#madesa-product-list') || root?.querySelector('.madesa-product-list');

    try {
      sortMadesaSearchProductCards(list);
    } finally {
      if (list) syncSearchResultSectionHeadings(list);
    }
  }

  /**
   * Reorder predictive search product rows (theme modal); skips recently-viewed block.
   * @param {HTMLElement} container
   * @param {string} query
   */
  function reorderPredictiveSearchProducts(container, query) {
    if (!(container instanceof HTMLElement)) return;
    const tokens = tokenizeQuery(query);
    if (tokens.length <= 1) return;

    const lists = container.querySelectorAll('.predictive-search-results__wrapper-products');
    lists.forEach((ul) => {
      if (ul.closest('.recently-viewed-wrapper')) return;

      const items = Array.from(ul.querySelectorAll(':scope > .predictive-search-results__card--product'));
      if (items.length <= 1) return;

      const scored = items.map((li, index) => {
        const title =
          li.querySelector('.resource-card__title')?.textContent ||
          li.querySelector('.product-card__title')?.textContent ||
          '';
        const snippet = (li.textContent || '').slice(0, 600);
        const hay = normalize(`${title} ${snippet}`);
        const titleNorm = normalize(title);
        const matches = countTokenMatches(hay, tokens);
        const titleMatches = countTokenMatches(titleNorm, tokens);
        const all = matches === tokens.length ? 1 : 0;
        const score = all * 1e9 + matches * 1e6 + titleMatches * 1e3 - index * 0.001;
        return { li, score };
      });

      scored.sort((a, b) => b.score - a.score);

      const itemKey = (li) => li.querySelector('a[href]')?.getAttribute('href') || li.dataset.resourceId || '';
      const newKey = scored.map(({ li }) => itemKey(li)).join('\u001d');
      const curKey = items.map((li) => itemKey(li)).join('\u001d');
      if (newKey === curKey) return;

      for (let i = 0; i < scored.length; i++) ul.appendChild(scored[i].li);
    });
  }

  /** @type {number | undefined} */
  let debounceTimer;
  /** @type {MutationObserver | undefined} */
  let productListObserver;

  function scheduleReorderMadesaSearchCards() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      reorderMadesaSearchCards();
    }, 120);
  }

  /**
   * Filters only replace-all updates from filters (innerHTML swap), not attribute tweaks.
   * Reordering nodes must not schedule another pass (handled inside reorder via order compare).
   */
  function isArticleSearchPage() {
    const type = new URLSearchParams(global.location.search).get('type') || '';
    return type === 'article';
  }

  function initSearchResultsPageObserver() {
    if (isArticleSearchPage()) return;

    const root = document.querySelector('results-list.madesa-search-results');
    const list = root?.querySelector('#madesa-product-list') || root?.querySelector('.madesa-product-list');
    if (!list || list.dataset.madesaSearchReorderInit === '1') return;
    list.dataset.madesaSearchReorderInit = '1';

    reorderMadesaSearchCards();
    scheduleSupplement(list);

    productListObserver?.disconnect();
    productListObserver = new MutationObserver((records) => {
      const relevant = records.some((r) => {
        for (let i = 0; i < r.addedNodes.length; i++) {
          const n = r.addedNodes[i];
          if (n.nodeType === 1 && /** @type {Element} */ (n).classList?.contains('madesa-product-card')) {
            return true;
          }
        }
        for (let j = 0; j < r.removedNodes.length; j++) {
          const n = r.removedNodes[j];
          if (n.nodeType === 1 && /** @type {Element} */ (n).classList?.contains('madesa-product-card')) {
            return true;
          }
        }
        return false;
      });
      if (!relevant) return;
      scheduleReorderMadesaSearchCards();
    });
    productListObserver.observe(list, { childList: true, subtree: false });

    list.addEventListener('madesa:search-list-refresh', () => {
      scheduleSupplement(list);
    });
  }

  global.MadesaSearchRelevance = {
    normalizeSearchQuery,
    handleize,
    buildProductSearchUrl,
    getCollectionHandleForQuery,
    navigateForSearchQuery,
    tokenizeQuery,
    sortProductsByQuery,
    reorderMadesaSearchCards,
    reorderPredictiveSearchProducts,
    initSearchResultsPageObserver,
    syncSearchResultSectionHeadings,
    sortMadesaSearchProductCards,
    supplementPartialSearchResults,
  };

  if (typeof document !== 'undefined') {
    function onSearchDomReady() {
      initSearchPageUrlSync().finally(() => {
        initSearchResultsPageObserver();
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', onSearchDomReady);
    } else {
      onSearchDomReady();
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
